<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useViperConnect } from 'dashboard/composables/useViperConnect';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const inboxId = computed(() => props.inbox.id);

const {
  status,
  instanceName,
  connectedAt,
  lastCheckAt,
  settings,
  qrCode,
  qrTimeRemaining,
  isLoading,
  isCreating,
  isTesting,
  error,
  isConnected,
  isDisconnected,
  isQrPending,
  isNotCreated,
  statusLabel,
  statusColor,
  fetchStatus,
  createInstance,
  refreshQrCode,
  testConnection,
  updateSettings,
  disconnect,
  startQrPolling,
  stopQrPolling,
} = useViperConnect(inboxId);

const showDisconnectConfirm = ref(false);

// Settings form
const formSettings = ref({
  always_online: true,
  read_messages: true,
  read_status: false,
  human_takeover_pauses_bot: true,
});

// Sync settings when loaded
watch(settings, (newSettings) => {
  formSettings.value = { ...newSettings };
}, { immediate: true });

// Methods
async function handleCreate() {
  // Use form settings for creation
  settings.value = { ...formSettings.value };
  await createInstance();
}

async function handleRefreshQr() {
  await refreshQrCode();
}

async function handleTest() {
  const success = await testConnection();
  if (success) {
    useAlert(t('INBOX_MGMT.VIPER_CONNECT.TEST_SUCCESS'));
  } else {
    useAlert(t('INBOX_MGMT.VIPER_CONNECT.TEST_FAILED'));
  }
}

async function handleSaveSettings() {
  settings.value = { ...formSettings.value };
  const success = await updateSettings();
  if (success) {
    useAlert(t('INBOX_MGMT.VIPER_CONNECT.SETTINGS_SAVED'));
  }
}

function handleDisconnectClick() {
  showDisconnectConfirm.value = true;
}

// Reconnect attempts to regenerate QR code for existing instance
// If instance was deleted, it will create a new one automatically
async function handleReconnect() {
  settings.value = { ...formSettings.value };
  await createInstance();
}

async function confirmDisconnect() {
  await disconnect();
  showDisconnectConfirm.value = false;
  useAlert(t('INBOX_MGMT.VIPER_CONNECT.DISCONNECTED'));
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('pt-BR');
}

// Watch for status changes to start/stop QR polling
watch(status, (newStatus) => {
  if (newStatus === 'qr_pending') {
    startQrPolling();
  } else {
    stopQrPolling();
  }
});

// Listen for Evolution status changes via ActionCable
function handleEvolutionStatusChange(eventData) {
  if (eventData?.data?.inbox_id === inboxId.value) {
    const newStatus = eventData?.data?.status;
    if (newStatus === 'connected') {
      status.value = 'connected';
      stopQrPolling();
      fetchStatus(); // Refresh all data
    } else if (newStatus === 'disconnected') {
      status.value = 'disconnected';
    }
  }
}

onMounted(() => {
  fetchStatus();
  emitter.on(BUS_EVENTS.EVOLUTION_STATUS_CHANGED, handleEvolutionStatusChange);
});

onUnmounted(() => {
  stopQrPolling();
  emitter.off(BUS_EVENTS.EVOLUTION_STATUS_CHANGED, handleEvolutionStatusChange);
});
</script>

<template>
  <div class="mx-8 py-6">
    <div class="flex items-center gap-3 mb-6">
      <div class="p-2 bg-woot-50 dark:bg-woot-800 rounded-lg">
        <fluent-icon icon="link" size="24" class="text-woot-500" />
      </div>
      <div>
        <h2 class="text-lg font-semibold text-slate-800 dark:text-slate-100">
          {{ t('INBOX_MGMT.VIPER_CONNECT.TITLE') }}
        </h2>
        <p class="text-sm text-slate-500 dark:text-slate-400">
          {{ t('INBOX_MGMT.VIPER_CONNECT.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="isLoading && !qrCode" class="flex justify-center py-12">
      <Spinner :size="32" />
    </div>

    <!-- Error message -->
    <div
      v-if="error"
      class="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
    >
      <p class="text-red-700 dark:text-red-400">{{ error }}</p>
    </div>

    <!-- State: Not Created -->
    <div
      v-if="isNotCreated && !isLoading"
      class="bg-slate-50 dark:bg-slate-800 rounded-xl p-8 text-center"
    >
      <div class="max-w-md mx-auto">
        <div class="w-16 h-16 mx-auto mb-4 bg-woot-100 dark:bg-woot-900 rounded-full flex items-center justify-center">
          <fluent-icon icon="add" size="32" class="text-woot-500" />
        </div>
        <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-2">
          {{ t('INBOX_MGMT.VIPER_CONNECT.NOT_CONFIGURED') }}
        </h3>
        <p class="text-slate-500 dark:text-slate-400 mb-6">
          {{ t('INBOX_MGMT.VIPER_CONNECT.NOT_CONFIGURED_DESC') }}
        </p>

        <!-- Settings before creation -->
        <div class="bg-white dark:bg-slate-700 rounded-lg p-4 mb-6 text-left">
          <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
            {{ t('INBOX_MGMT.VIPER_CONNECT.SETTINGS_TITLE') }}
          </h4>
          <div class="space-y-3">
            <label class="flex items-center gap-3 cursor-pointer">
              <input
                v-model="formSettings.always_online"
                type="checkbox"
                class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
              />
              <span class="text-sm text-slate-700 dark:text-slate-300">
                {{ t('INBOX_MGMT.VIPER_CONNECT.ALWAYS_ONLINE') }}
              </span>
            </label>
            <label class="flex items-center gap-3 cursor-pointer">
              <input
                v-model="formSettings.read_messages"
                type="checkbox"
                class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
              />
              <span class="text-sm text-slate-700 dark:text-slate-300">
                {{ t('INBOX_MGMT.VIPER_CONNECT.READ_MESSAGES') }}
              </span>
            </label>
            <label class="flex items-center gap-3 cursor-pointer">
              <input
                v-model="formSettings.read_status"
                type="checkbox"
                class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
              />
              <span class="text-sm text-slate-700 dark:text-slate-300">
                {{ t('INBOX_MGMT.VIPER_CONNECT.READ_STATUS') }}
              </span>
            </label>
            <!-- Human Takeover Setting -->
            <div class="pt-3 mt-3 border-t border-slate-200 dark:border-slate-600">
              <label class="flex items-center gap-3 cursor-pointer">
                <input
                  v-model="formSettings.human_takeover_pauses_bot"
                  type="checkbox"
                  class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
                />
                <div>
                  <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {{ t('INBOX_MGMT.VIPER_CONNECT.HUMAN_TAKEOVER') }}
                  </span>
                  <p class="text-xs text-slate-500">
                    {{ t('INBOX_MGMT.VIPER_CONNECT.HUMAN_TAKEOVER_DESC') }}
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>

        <NextButton
          :label="t('INBOX_MGMT.VIPER_CONNECT.CREATE_CONNECTION')"
          :is-loading="isCreating"
          icon="add"
          @click="handleCreate"
        />
      </div>
    </div>

    <!-- State: QR Pending -->
    <div
      v-if="isQrPending"
      class="bg-slate-50 dark:bg-slate-800 rounded-xl p-8 text-center"
    >
      <div class="max-w-md mx-auto">
        <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-2">
          {{ t('INBOX_MGMT.VIPER_CONNECT.SCAN_QR') }}
        </h3>
        <p class="text-slate-500 dark:text-slate-400 mb-6">
          {{ t('INBOX_MGMT.VIPER_CONNECT.SCAN_QR_DESC') }}
        </p>

        <!-- QR Code -->
        <div class="bg-white dark:bg-slate-700 rounded-xl p-4 mb-4 inline-block">
          <img
            v-if="qrCode"
            :src="qrCode"
            alt="QR Code"
            class="w-64 h-64"
          />
          <div v-else class="w-64 h-64 flex items-center justify-center">
            <Spinner :size="48" />
          </div>
        </div>

        <!-- Timer -->
        <div class="flex items-center justify-center gap-2 mb-4 text-sm">
          <fluent-icon icon="clock" size="16" class="text-slate-400" />
          <span class="text-slate-500 dark:text-slate-400">
            {{ t('INBOX_MGMT.VIPER_CONNECT.EXPIRES_IN') }}:
            <span
              :class="{
                'text-red-500': qrTimeRemaining <= 15,
                'text-yellow-500': qrTimeRemaining > 15 && qrTimeRemaining <= 30,
                'text-slate-700 dark:text-slate-300': qrTimeRemaining > 30
              }"
            >
              {{ qrTimeRemaining }}s
            </span>
          </span>
        </div>

        <NextButton
          :label="t('INBOX_MGMT.VIPER_CONNECT.REFRESH_QR')"
          :is-loading="isLoading"
          icon="arrow-sync"
          variant="outline"
          color="slate"
          @click="handleRefreshQr"
        />
      </div>
    </div>

    <!-- State: Connected -->
    <div
      v-if="isConnected"
      class="space-y-6"
    >
      <!-- Status Card -->
      <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
            <fluent-icon icon="checkmark" size="24" class="text-green-600 dark:text-green-400" />
          </div>
          <div class="flex-1">
            <h3 class="text-lg font-medium text-green-800 dark:text-green-300">
              {{ t('INBOX_MGMT.VIPER_CONNECT.CONNECTED') }}
            </h3>
            <div class="text-sm text-green-600 dark:text-green-400 space-y-1">
              <p>{{ t('INBOX_MGMT.VIPER_CONNECT.INSTANCE') }}: {{ instanceName }}</p>
              <p>{{ t('INBOX_MGMT.VIPER_CONNECT.CONNECTED_AT') }}: {{ formatDate(connectedAt) }}</p>
              <p>{{ t('INBOX_MGMT.VIPER_CONNECT.LAST_CHECK') }}: {{ formatDate(lastCheckAt) }}</p>
            </div>
          </div>
          <NextButton
            :label="t('INBOX_MGMT.VIPER_CONNECT.TEST_CONNECTION')"
            :is-loading="isTesting"
            variant="outline"
            color="slate"
            size="sm"
            @click="handleTest"
          />
        </div>
      </div>

      <!-- Settings Card -->
      <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
        <h4 class="text-base font-medium text-slate-800 dark:text-slate-100 mb-4">
          {{ t('INBOX_MGMT.VIPER_CONNECT.SETTINGS_TITLE') }}
        </h4>
        <div class="space-y-3 mb-6">
          <label class="flex items-center gap-3 cursor-pointer">
            <input
              v-model="formSettings.always_online"
              type="checkbox"
              class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
            />
            <div>
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ t('INBOX_MGMT.VIPER_CONNECT.ALWAYS_ONLINE') }}
              </span>
              <p class="text-xs text-slate-500">
                {{ t('INBOX_MGMT.VIPER_CONNECT.ALWAYS_ONLINE_DESC') }}
              </p>
            </div>
          </label>
          <label class="flex items-center gap-3 cursor-pointer">
            <input
              v-model="formSettings.read_messages"
              type="checkbox"
              class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
            />
            <div>
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ t('INBOX_MGMT.VIPER_CONNECT.READ_MESSAGES') }}
              </span>
              <p class="text-xs text-slate-500">
                {{ t('INBOX_MGMT.VIPER_CONNECT.READ_MESSAGES_DESC') }}
              </p>
            </div>
          </label>
          <label class="flex items-center gap-3 cursor-pointer">
            <input
              v-model="formSettings.read_status"
              type="checkbox"
              class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
            />
            <div>
              <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                {{ t('INBOX_MGMT.VIPER_CONNECT.READ_STATUS') }}
              </span>
              <p class="text-xs text-slate-500">
                {{ t('INBOX_MGMT.VIPER_CONNECT.READ_STATUS_DESC') }}
              </p>
            </div>
          </label>

          <!-- Human Takeover Setting -->
          <div class="pt-3 mt-3 border-t border-slate-200 dark:border-slate-600">
            <label class="flex items-center gap-3 cursor-pointer">
              <input
                v-model="formSettings.human_takeover_pauses_bot"
                type="checkbox"
                class="w-4 h-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
              />
              <div>
                <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {{ t('INBOX_MGMT.VIPER_CONNECT.HUMAN_TAKEOVER') }}
                </span>
                <p class="text-xs text-slate-500">
                  {{ t('INBOX_MGMT.VIPER_CONNECT.HUMAN_TAKEOVER_DESC') }}
                </p>
              </div>
            </label>
          </div>
        </div>
        <div class="flex gap-3">
          <NextButton
            :label="t('INBOX_MGMT.VIPER_CONNECT.SAVE_SETTINGS')"
            :is-loading="isLoading"
            @click="handleSaveSettings"
          />
          <NextButton
            :label="t('INBOX_MGMT.VIPER_CONNECT.DISCONNECT')"
            color="ruby"
            @click="handleDisconnectClick"
          />
        </div>
      </div>
    </div>

    <!-- State: Disconnected -->
    <div
      v-if="isDisconnected"
      class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-8 text-center"
    >
      <div class="max-w-md mx-auto">
        <div class="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
          <fluent-icon icon="dismiss" size="32" class="text-red-500" />
        </div>
        <h3 class="text-lg font-medium text-red-800 dark:text-red-300 mb-2">
          {{ t('INBOX_MGMT.VIPER_CONNECT.DISCONNECTED_TITLE') }}
        </h3>
        <p class="text-red-600 dark:text-red-400 mb-6">
          {{ t('INBOX_MGMT.VIPER_CONNECT.DISCONNECTED_DESC') }}
        </p>
        <div class="flex justify-center gap-3">
          <NextButton
            :label="t('INBOX_MGMT.VIPER_CONNECT.RECONNECT')"
            :is-loading="isCreating"
            icon="arrow-sync"
            @click="handleReconnect"
          />
          <NextButton
            :label="t('INBOX_MGMT.VIPER_CONNECT.DELETE_INSTANCE')"
            color="ruby"
            @click="handleDisconnectClick"
          />
        </div>
      </div>
    </div>

    <!-- Disconnect Confirmation Modal -->
    <woot-modal
      v-model:show="showDisconnectConfirm"
      :on-close="() => showDisconnectConfirm = false"
    >
      <div class="flex flex-col h-auto overflow-auto">
        <woot-modal-header
          :header-title="t('INBOX_MGMT.VIPER_CONNECT.CONFIRM_DISCONNECT')"
        />
        <div class="p-6">
          <p class="text-slate-500 dark:text-slate-400 mb-6">
            {{ t('INBOX_MGMT.VIPER_CONNECT.CONFIRM_DISCONNECT_DESC') }}
          </p>
          <div class="flex justify-end gap-3">
          <NextButton
            :label="t('INBOX_MGMT.VIPER_CONNECT.CANCEL')"
            variant="outline"
            color="slate"
            @click="showDisconnectConfirm = false"
          />
          <NextButton
            :label="t('INBOX_MGMT.VIPER_CONNECT.CONFIRM')"
            color="ruby"
            :is-loading="isLoading"
            @click="confirmDisconnect"
          />
          </div>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

